import { IPromise, IQService, IScope } from 'angular';
import {
    getStep,
    IOrder,
    IOrderSummary,
    LicenseType,
    OrderFilters,
    orderHasLicenseType,
    OrderService,
    tnEquals,
} from '../../../tn.common';
import template from './accountOrdersSidebar.html';

export interface ILicenseTypeOption {
    id: LicenseType;
    label: string;
    count: number;
}
export interface ILicenseeOption {
    id: string;
    count: number;
}

export const defaultLicenseTypeOptions: ILicenseTypeOption[] = [
    { id: 'desktop', label: 'Desktop', count: 0 },
    { id: 'web', label: 'Web', count: 0 },
    { id: 'app', label: 'Application', count: 0 },
    { id: 'epub', label: 'Epub', count: 0 },
    { id: 'broadcast', label: 'Broadcast', count: 0 },
    { id: 'server', label: 'Server', count: 0 },
    { id: 'game', label: 'Game', count: 0 },
    { id: 'embedding', label: 'Embedding', count: 0 },
];

export class AccountOrdersSidebarController {
    // data
    summaries: IOrderSummary[] = [];

    // used to show counts and such.
    filteredOrders?: IOrderSummary[] = [];

    // control options
    minDate?: Date;
    maxDate?: Date;

    priceMin = 0;
    priceMax = 0;
    priceStep = 0;

    licenseTypeOptions: ILicenseTypeOption[] = [...defaultLicenseTypeOptions];
    licenseeOptions: ILicenseeOption[] = [];

    licenseeFilterActive = false;
    licenseTypeFilterActive = false;

    // convenience varaible for use in templates.
    currentDate = new Date();

    // filter values
    startDate?: Date;
    endDate?: Date;
    costMin?: number;
    costMax?: number;
    licensees?: string[];
    licenseTypes?: LicenseType[];

    static $inject = ['$q', '$scope', 'accountOrderFilters', 'orderService'];
    constructor(
        protected $q: IQService,
        protected $scope: IScope,
        protected filters$: OrderFilters,
        protected orderService: OrderService
    ) {}
    

    $onInit(): void {
        this.filters$.subscribe((filters) => {
            this.orderService
                .getSummaryOrders(filters)
                .then((filteredSummaries) => {
                    this.filteredOrders = filteredSummaries;
                });
        });
        this._getData();
    }

    _getData(): IPromise<IOrder[]> {
        return this.orderService.getSummaryOrders({}).then((summaries) => {
            this.summaries = summaries;
            const licensees = {};
            
            this.licenseTypeOptions = Object.assign(
                {},
                defaultLicenseTypeOptions
            );
            let minDate = new Date();
            let maxDate = new Date(2014);
            let priceMax = 0;
            let priceMin = 0;

            // build license options
            summaries.forEach((summary) => {
                if (summary.total > priceMax) priceMax = summary.total;
                if (summary.total < priceMin) priceMin = summary.total;
                if (summary.created < minDate) minDate = summary.created;
                if (summary.created > maxDate) maxDate = summary.created;
                // licensees
                if (!licensees[summary.licensee]) {
                    licensees[summary.licensee] = {
                        id: summary.licensee,
                        count: 1,
                    };
                } else {
                    licensees[summary.licensee].count++;
                }
            });

            // set minDate and maxDate  to the beginning of the year prior and the end of the year after the min and mac
            // to ensure our options always exceed our set.
            this.minDate = new Date(minDate.getFullYear() - 1, 0);
            this.maxDate = new Date(maxDate.getFullYear() + 1, 12, 0);

            this.priceMin = priceMin;
            this.priceMax = priceMax;
            this.priceStep = getStep(priceMin, priceMax);
            if (!this.costMin) this.costMin = this.priceMin;
            if (!this.costMax) this.costMax = this.priceMax;

            this.licenseeOptions = Object.keys(licensees)
                .sort()
                .map((licensee) => licensees[licensee]);

            const promises = summaries.map((order) =>
                this.orderService.getOrder(order.id)
            );
            return this.$q.all(promises).then((orders) => {
                this.licenseTypeOptions = [...defaultLicenseTypeOptions];
                orders.forEach((order) => {
                    this.licenseTypeOptions.forEach((type) => {
                        if (orderHasLicenseType(order, type.id)) type.count++;
                    });
                });
                return orders;
            });
        });
    }

    clearFilters(): void {
        delete this.startDate;
        delete this.endDate;
        this.costMin = this.priceMin;
        this.costMax = this.priceMax;
        this.filters$.next({});
    }

    onStartDateChanged(value: Date): void {
        const filters = this.filters$.getValue();
        if (!tnEquals(filters.startDate, value)) {
            // start date will default to BOM.
            filters.startDate = value;
            this.filters$.next(filters);
        }
    }

    onEndDateChanged(value: Date): void {
        const filters = this.filters$.getValue();
        if (!tnEquals(filters.endDate, value)) {
            // set day to EOM
            const endDate = new Date(
                value.getFullYear(),
                value.getMonth() + 1,
                0
            );
            filters.endDate = endDate;
            this.filters$.next(filters);
        }
    }

    onCostChanged(): void {
        const filters = this.filters$.getValue();
        if (
            filters.costMax != this.costMax ||
            filters.costMin != this.costMin
        ) {
            filters.costMax = this.costMax;
            filters.costMin = this.costMin;
            this.filters$.next(filters);
        }
    }

    onClickLicensee(licensee: ILicenseeOption): void {
        const filters = this.filters$.getValue();
        const id = licensee.id;
        if (!filters.licensees) {
            filters.licensees = {};
        }
        if (filters.licensees[id]) delete filters.licensees[id];
        else filters.licensees[id] = true;
        this.licenseeFilterActive =
            !!filters.licensees && Object.keys(filters.licensees).length > 0;
        // console.log('onClickLicensee', licensee, this.licenseeFilterActive);

        this.filters$.next(filters);
    }

    isLicenseeSelected(licensee: ILicenseeOption): boolean {
        const filters = this.filters$.getValue();
        return !!filters.licensees && !!filters.licensees[licensee.id];
    }

    onClickLicenseType(licenseType: ILicenseTypeOption): void {
        const filters = this.filters$.getValue();
        const id = licenseType.id;
        if (!filters.licenseTypes) {
            filters.licenseTypes = {};
        }
        if (filters.licenseTypes[id]) delete filters.licenseTypes[id];
        else filters.licenseTypes[id] = true;
        this.licenseTypeFilterActive =
            !!filters.licenseTypes &&
            Object.keys(filters.licenseTypes).length > 0;
        // console.log('onClickLicenseType', licenseType, this.licenseTypes);
        this.filters$.next(filters);
    }

    isLicenseTypeSelect(licenseType: ILicenseTypeOption): boolean {
        const filters = this.filters$.getValue();
        return !!filters.licenseTypes && !!filters.licenseTypes[licenseType.id];
    }
}

export const accountOrdersSidebarComponentOptions = {
    template,
    controller: AccountOrdersSidebarController,
};
